import { useMemo, useState } from "react";
import {
  api,
  type Classification,
  type EngagementParts,
  type OverrideRecord,
  type OverrideRegister,
  type TeamMember,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  Select,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { day, stamp } from "../lib/format";

/**
 * The override register — the exceptions, and why each one was made.
 *
 * Two instruments, opposite in direction and identical in form.
 *
 *   **Grant** (rule 7) lifts one named object above one person's ceiling,
 *   for a stated reason, until a stated date. The narrowest thing in the
 *   model: never a type, never in bulk, never Highly Restricted, never
 *   open-ended. If somebody needs a whole class of material, that is a
 *   change of role or of assignment scope, not a grant.
 *
 *   **Deny** (rule 4) is the sharpest. It beats reach, classification,
 *   inheritance and any grant — everything. Which is exactly why getting one
 *   wrong is expensive, and why lifting is a first-class act rather than a
 *   deletion.
 *
 * Neither is ever edited and neither is ever deleted. Changing your mind
 * means withdrawing one and issuing another, so the register shows both
 * decisions rather than only the latest. The application holds no DELETE
 * privilege on either table; it is not a convention, it is a revoked grant
 * in migration 012.
 *
 * **The history is the whole point.** A register showing only what is
 * currently in force cannot answer "who has ever been given access to this",
 * which is the question an audit actually asks. So withdrawn entries stay,
 * greyed, with who withdrew them and why.
 *
 * Authority here is `app.may_manage` — a Lead on this engagement, a
 * Principal Partner, a Super Admin. A consultant opening this sees their own
 * overrides and nothing else, which is deliberate: being refused something
 * without being able to learn that you were is worse than the refusal.
 */

/** What an override may be written against. The server holds the same list. */
const OVERRIDABLE: Array<{ value: string; label: string }> = [
  { value: "document", label: "A document" },
  { value: "deliverable", label: "A deliverable" },
  { value: "workstream", label: "A workstream" },
  { value: "milestone", label: "A milestone" },
  { value: "task", label: "A task" },
  { value: "comment", label: "A comment" },
];

const GRANTABLE: Array<{ value: Classification; label: string }> = [
  { value: "client_visible", label: "Client visible" },
  { value: "internal", label: "Internal" },
  { value: "restricted", label: "Restricted" },
];

export function AccessRegister({
  engagementId,
  parts,
  team,
}: {
  engagementId: string;
  parts?: EngagementParts;
  team: TeamMember[];
}) {
  const register = useResource<OverrideRegister>(
    () => api.get<OverrideRegister>(`/api/engagements/${engagementId}/overrides`),
    [engagementId],
  );

  const [writing, setWriting] = useState<"grant" | "deny" | null>(null);
  const [showWithdrawn, setShowWithdrawn] = useState(false);

  const data =
    register.resource.state === "ready"
      ? register.resource.data
      : { grants: [], denies: [] };

  const live = useMemo(
    () => [...data.denies.filter((d) => d.live), ...data.grants.filter((g) => g.live)],
    [data],
  );
  const withdrawn = useMemo(
    () => [
      ...data.denies.filter((d) => !d.live),
      ...data.grants.filter((g) => !g.live),
    ],
    [data],
  );

  // Only people who are actually on the engagement. An override against
  // somebody who was never here is not a thing anyone means to write, and
  // offering the whole directory would invite it.
  const subjects = useMemo(
    () => team.filter((m) => m.kind !== "client"),
    [team],
  );

  return (
    <section className="mt-14 border-t border-line-strong pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <Label as="h2">Exceptions</Label>
          <p className="mt-1 max-w-[62ch] text-sm text-secondary">
            Temporary access changes and blocks for this engagement. Every
            change records who made it, when, and why.
          </p>
        </div>
        <p className="text-sm text-secondary">
          {live.length} in force
          {withdrawn.length > 0 && ` · ${withdrawn.length} withdrawn`}
        </p>
      </div>

      {register.resource.state === "loading" && <SkeletonRows rows={2} />}
      {register.resource.state === "failed" && (
        <Failed
          message={register.resource.message}
          correlationId={register.resource.correlationId}
        />
      )}

      {register.resource.state === "ready" && (
        <>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button tone="quiet" onClick={() => setWriting(writing === "grant" ? null : "grant")}>
              Give temporary access
            </Button>
            <Button tone="quiet" onClick={() => setWriting(writing === "deny" ? null : "deny")}>
              Block access
            </Button>
          </div>

          {writing === "grant" && (
            <WriteGrant
              engagementId={engagementId}
              subjects={subjects}
              parts={parts}
              onDone={() => {
                setWriting(null);
                register.reload();
              }}
            />
          )}
          {writing === "deny" && (
            <WriteDeny
              engagementId={engagementId}
              subjects={subjects}
              parts={parts}
              onDone={() => {
                setWriting(null);
                register.reload();
              }}
            />
          )}

          {live.length === 0 ? (
            <Empty title="No exceptions">
              Everyone has the standard access for their role and assignment.
            </Empty>
          ) : (
            <ul className="mt-6">
              {live.map((r) => (
                <OverrideRow
                  key={r.id}
                  record={r}
                  kind={data.denies.some((d) => d.id === r.id) ? "deny" : "grant"}
                  onChanged={register.reload}
                />
              ))}
            </ul>
          )}

          {withdrawn.length > 0 && (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => setShowWithdrawn((v) => !v)}
                className="label underline underline-offset-4 hover:text-ink"
              >
                {showWithdrawn ? "Hide" : "Show"} {withdrawn.length} withdrawn
              </button>
              {showWithdrawn && (
                <ul className="mt-4">
                  {withdrawn.map((r) => (
                    <OverrideRow
                      key={r.id}
                      record={r}
                      kind={data.denies.some((d) => d.id === r.id) ? "deny" : "grant"}
                      onChanged={register.reload}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** One line of the register, in force or not. */
function OverrideRow({
  record: r,
  kind,
  onChanged,
}: {
  record: OverrideRecord;
  kind: "grant" | "deny";
  onChanged: () => void;
}) {
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");

  const withdraw = useAction(async () => {
    await api.post(
      kind === "grant" ? `/api/grants/${r.id}/revoke` : `/api/denies/${r.id}/lift`,
      { reason: reason.trim() },
    );
    setWithdrawing(false);
    setReason("");
    onChanged();
  });

  // A grant whose date has simply passed was not withdrawn by anybody, and
  // saying "withdrawn" of it would put an act on the record that nobody
  // performed. Expiry is the terms working, not a decision.
  const expired = !r.live && !r.withdrawnAt;

  return (
    <li className={"border-b border-line py-4 " + (r.live ? "" : "opacity-60")}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-2">
        <div className="min-w-0">
          <p className="text-base text-ink">
            <Mark tone={kind === "deny" ? "danger" : "warning"}>
              {kind === "deny" ? "Blocked" : "Extra access"}
            </Mark>{" "}
            <span className="ml-2">{r.userName ?? "Someone no longer listed"}</span>
          </p>
          <p className="mt-1 text-sm text-secondary">
            {r.objectName ? (
              <>
                {labelOf(r.objectType)} · {r.objectName}
              </>
            ) : r.objectId ? (
              // The thing exists and this reader cannot see it. Saying so is
              // better than printing a uuid as though it were a name.
              <>{labelOf(r.objectType)} · not visible to you</>
            ) : (
              <>Every {r.objectType} on this engagement</>
            )}
            {r.maxClassification && ` · up to ${classLabel(r.maxClassification)}`}
          </p>
          <p className="mt-2 max-w-[62ch] text-sm text-ink">{r.reason}</p>
        </div>

        <div className="text-right text-sm text-secondary">
          <p>
            {r.issuedByName ?? "Someone no longer listed"} · {day(r.issuedAt)}
          </p>
          {r.live && r.expiresAt && <p className="mt-1">until {day(r.expiresAt)}</p>}
          {expired && <p className="mt-1">lapsed {day(r.expiresAt ?? r.issuedAt)}</p>}
        </div>
      </div>

      {r.withdrawnAt && (
        <p className="mt-3 border-l-2 border-line pl-4 text-sm text-secondary">
          {kind === "grant" ? "Revoked" : "Lifted"} by{" "}
          {r.withdrawnByName ?? "someone no longer listed"} on{" "}
          {stamp(r.withdrawnAt)} — {r.withdrawalReason}
        </p>
      )}

      {r.live && (
        <div className="mt-3">
          {withdrawing ? (
            <div className="flex flex-wrap items-end gap-3">
              <Field
                label={kind === "grant" ? "Why revoke it" : "Why lift it"}
                hint="It goes on the record beside the original reason."
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button
                tone="primary"
                disabled={!reason.trim()}
                loading={withdraw.pending}
                onClick={() => void withdraw.invoke()}
              >
                {kind === "grant" ? "Revoke it" : "Lift it"}
              </Button>
              <Button tone="quiet" onClick={() => setWithdrawing(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button tone="quiet" onClick={() => setWithdrawing(true)}>
              {kind === "grant" ? "Revoke" : "Lift"}
            </Button>
          )}
        </div>
      )}

      {withdraw.refusal && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {withdraw.refusal.message}
        </p>
      )}
    </li>
  );
}

/**
 * Issuing a grant.
 *
 * Every field is required and the end date is the one people would leave
 * blank if they could. An open-ended exception is not an exception; it is a
 * permission change made where nobody will look for it.
 */
function WriteGrant({
  engagementId,
  subjects,
  parts,
  onDone,
}: {
  engagementId: string;
  subjects: TeamMember[];
  parts?: EngagementParts;
  onDone: () => void;
}) {
  const [userId, setUserId] = useState("");
  const [objectType, setObjectType] = useState("document");
  const [objectId, setObjectId] = useState("");
  const [maxClassification, setMax] = useState<Classification>("restricted");
  const [reason, setReason] = useState("");
  const [expiresOn, setExpiresOn] = useState("");

  const choices = objectsOfType(objectType, parts);

  const issue = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/grants`, {
      userId,
      objectType,
      objectId,
      maxClassification,
      reason: reason.trim(),
      expiresOn,
    });
    onDone();
  });

  return (
    <div className="mt-5 grid max-w-3xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <p className="text-sm text-secondary sm:col-span-2">
        Give one person temporary access to one specific item. Use their role
        or assignment when they need broader access.
      </p>

      <Select label="Who" value={userId} onChange={(e) => setUserId(e.target.value)}>
        <option value="">Choose a person</option>
        {subjects.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>

      <Select
        label="What kind of thing"
        value={objectType}
        onChange={(e) => {
          setObjectType(e.target.value);
          setObjectId("");
        }}
      >
        {OVERRIDABLE.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>

      {choices ? (
        <Select
          label="Which one"
          value={objectId}
          onChange={(e) => setObjectId(e.target.value)}
        >
          <option value="">Choose one</option>
          {choices.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      ) : (
        <Field
          label="Which one"
          hint="Enter the item reference. Documents and deliverables appear in the list when available."
          value={objectId}
          onChange={(e) => setObjectId(e.target.value)}
        />
      )}

      <Select
        label="Access level"
        value={maxClassification}
        onChange={(e) => setMax(e.target.value as Classification)}
      >
        {GRANTABLE.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </Select>

      <Field
        label="Until"
        type="date"
        hint="Required. Temporary access must have an end date."
        value={expiresOn}
        onChange={(e) => setExpiresOn(e.target.value)}
      />

      <div className="sm:col-span-2">
        <TextArea
          label="Why"
          hint="Explain why this access is needed."
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <div className="sm:col-span-2">
        <Button
          tone="primary"
          loading={issue.pending}
          disabled={!userId || !objectId || !reason.trim() || !expiresOn}
          onClick={() => void issue.invoke()}
        >
          Give temporary access
        </Button>
      </div>

      {issue.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {issue.refusal.message}
        </p>
      )}
    </div>
  );
}

/**
 * Recording a deny.
 *
 * Unlike a grant, a deny may name a whole type — "no documents at all",
 * which is the usual shape of a conflict of interest — and has no end date,
 * because it stands until somebody lifts it.
 */
function WriteDeny({
  engagementId,
  subjects,
  parts,
  onDone,
}: {
  engagementId: string;
  subjects: TeamMember[];
  parts?: EngagementParts;
  onDone: () => void;
}) {
  const [userId, setUserId] = useState("");
  const [objectType, setObjectType] = useState("document");
  const [objectId, setObjectId] = useState("");
  const [reason, setReason] = useState("");

  const choices = objectsOfType(objectType, parts);

  const record = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/denies`, {
      userId,
      objectType,
      objectId: objectId || null,
      reason: reason.trim(),
    });
    onDone();
  });

  return (
    <div className="mt-5 grid max-w-3xl gap-4 border-l-2 border-danger pl-4 sm:grid-cols-2">
      <p className="text-sm text-ink sm:col-span-2">
        A block removes this person's access even if their role or assignment
        would normally allow it. It remains until someone lifts it, and the
        reason stays in the history.
      </p>

      <Select label="Who" value={userId} onChange={(e) => setUserId(e.target.value)}>
        <option value="">Choose a person</option>
        {subjects.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>

      <Select
        label="What kind of thing"
        value={objectType}
        onChange={(e) => {
          setObjectType(e.target.value);
          setObjectId("");
        }}
      >
        {OVERRIDABLE.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>

      <div className="sm:col-span-2">
        {choices ? (
          <Select
            label="Which one"
            hint="Leave as every one to block access to this entire type on the engagement."
            value={objectId}
            onChange={(e) => setObjectId(e.target.value)}
          >
            <option value="">Every one on this engagement</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        ) : (
          <Field
            label="Which one"
            hint="Leave blank to block access to this entire type on the engagement."
            value={objectId}
            onChange={(e) => setObjectId(e.target.value)}
          />
        )}
      </div>

      <div className="sm:col-span-2">
        <TextArea
          label="Why"
          hint="Explain why this person should not have access."
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <div className="sm:col-span-2">
        <Button
          tone="danger"
          loading={record.pending}
          disabled={!userId || !reason.trim()}
          onClick={() => void record.invoke()}
        >
          Block access
        </Button>
      </div>

      {record.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {record.refusal.message}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------- helpers ----

/**
 * The things of a type that this screen can name, or null if it cannot.
 *
 * `EngagementParts` carries the workstreams and milestones; documents and
 * deliverables are not in it, so those fall back to a plain identifier
 * field rather than an empty dropdown that looks broken.
 */
function objectsOfType(
  type: string,
  parts?: EngagementParts,
): Array<{ id: string; name: string }> | null {
  if (!parts) return null;
  if (type === "workstream") {
    return parts.workstreams.map((w) => ({ id: w.id, name: w.name }));
  }
  if (type === "milestone") {
    return parts.milestones.map((m) => ({ id: m.id, name: m.name }));
  }
  return null;
}

function labelOf(type: string): string {
  return OVERRIDABLE.find((o) => o.value === type)?.label ?? type;
}

function classLabel(c: string): string {
  return c.replace(/_/g, " ");
}
